import { useState, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { useValidacaoPedido } from '@/hooks/useValidacaoPedido';
import { usePedidoMovement } from '@/hooks/usePedidoMovement';
import { AlertaItensIncompletosModal } from '@/components/pedido/modals/AlertaItensIncompletosModal';
import { ModalSucessoFechamento } from '@/components/pedido/modals/ModalSucessoFechamento';
import { EnviarBKOOverlay, EnviarBKOStep } from '@/components/pedido/EnviarBKOOverlay';
import { Send, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { usePainelPedido } from '@/contexts/PainelPedidoContext';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { isPedidoValido, logPedidoSeguro, formatPedidoIdParaLog } from '@/lib/pedidoValidation';
import { montarConvergencia, type ConvergenciaPayload } from '@/lib/convergencia';
import { avaliarConvergenciaPedido, gravarConvergencia, registrarTimelineConvergencia, statusAutoPassa, getConvergenciaPedido, statusJaResolvidoPassa, type AvaliacaoPedido } from '@/lib/convergenciaPedido';
import { ModalConvergencia, type ResolverConvergencia } from '@/components/pedido/modals/ModalConvergencia';
import { ehVendaNova, ehMigracao } from '@/lib/tipoDaLinha';

interface Props {
  pedido: any;
  label?: string;
}

// ============= CONSTANTES =============

const INITIAL_STEPS: EnviarBKOStep[] = [
  { id: 1, label: 'Validando dados do pedido', status: 'pending' },
  { id: 2, label: 'Criando pedido filho', status: 'pending' },
  { id: 3, label: 'Movendo itens', status: 'pending' },
  { id: 4, label: 'Atualizando status', status: 'pending' },
  { id: 5, label: 'Atribuindo BKO', status: 'pending' },
  { id: 6, label: 'Finalizando', status: 'pending' },
];

const TIMEOUT_GLOBAL_MS = 60000; // 60s timeout global

export function BotaoFecharPedido({ pedido, label }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const painelPedido = usePainelPedido();
  const { validarPedido, validando } = useValidacaoPedido();
  const { isMoving } = usePedidoMovement();
  
  // ✅ Estados de UI
  const [alertaOpen, setAlertaOpen] = useState(false);
  const [validacaoResultado, setValidacaoResultado] = useState<any>(null);
  const [modalSucessoOpen, setModalSucessoOpen] = useState(false);
  const [dadosSucesso, setDadosSucesso] = useState<{
    pedidoNumero: string;
    bkoNome: string | null;
    pedidoId: string;
  } | null>(null);

  // ✅ Convergência (trava no fechamento)
  const [convAval, setConvAval] = useState<AvaliacaoPedido | null>(null);
  const [modalConvOpen, setModalConvOpen] = useState(false);

  // ✅ Estados do Overlay
  const [isProcessing, setIsProcessing] = useState(false);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [steps, setSteps] = useState<EnviarBKOStep[]>(INITIAL_STEPS);
  const [runId, setRunId] = useState('');
  const [elapsedMs, setElapsedMs] = useState(0);
  const [overlayError, setOverlayError] = useState<string | null>(null);
  
  // ✅ Ref para proteção imediata contra duplo clique
  const isProcessingRef = useRef(false);
  const startTimeRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ✅ Verificar se o pedido já está fechado
  const { data: pedidoJaFechado, isLoading: checkingStatus } = useQuery({
    queryKey: ['check-pedido-fechado', pedido?.id],
    queryFn: async () => {
      try {
        if (!isPedidoValido(pedido)) {
          return false;
        }
        
        logPedidoSeguro('[BotaoFecharPedido] Verificando bloqueio', pedido);
        
        if (pedido.pedido_mae_id) {
          return true;
        }
        
        // ✅ Verificar filhos COM ITENS (filhos vazios/órfãos não bloqueiam)
        const { data: filhos, error: filhosError } = await supabase
          .from('pedidos')
          .select('id')
          .eq('pedido_mae_id', pedido.id);
        
        if (filhosError) throw filhosError;
        
        if (filhos && filhos.length > 0) {
          // Verificar se algum filho tem itens reais
          const filhoIds = filhos.map(f => f.id);
          const { data: itensFilhos } = await supabase
            .from('itens_pedido')
            .select('id')
            .in('pedido_id', filhoIds)
            .limit(1);
          
          if (itensFilhos && itensFilhos.length > 0) {
            return true; // Filho com itens → realmente fechado
          }
          // Filhos vazios → não bloquear
        }
        
        const colunasPosFechamento = [
          'PEDIDO FECHADO', 'PEDIDO RECUPERADO', 'CRIAÇÃO PEDIDO',
          'ACEITE', 'INPUT', 'INPUT REALIZADO MOVEL', 'INPUT REALIZADO FIXA', 'QUALIDADE', 'FORA SUL - CRIAR PEDIDO'
        ];
        
        const { data: itens, error: itensError } = await supabase
          .from('itens_pedido')
          .select('id, coluna_atual')
          .eq('pedido_id', pedido.id)
          .in('coluna_atual', colunasPosFechamento)
          .limit(1);
        
        if (itensError) throw itensError;
        
        return itens && itens.length > 0;
      } catch (error) {
        console.error('❌ [BotaoFecharPedido] Erro no queryFn:', error);
        return false;
      }
    },
    enabled: !!pedido?.id,
    staleTime: 5000,
    refetchOnMount: true,
  });

  // Convergência: o pedido já foi enviado para análise do supervisor? Se sim, o
  // botão vira "Em análise" (disabled) — antes ficava "Enviar ao BKO" ativo e o
  // vendedor reenviava achando que tinha falhado.
  const { data: convStatus } = useQuery({
    queryKey: ['convergencia-status-botao', pedido?.id],
    queryFn: () => getConvergenciaPedido(pedido.id),
    enabled: !!pedido?.id && !pedidoJaFechado,
    staleTime: 5000,
    refetchOnMount: true,
  });
  // Trava o fechamento só enquanto falta a decisão do supervisor. NÃO trava
  // por bko_validacao='pendente': com print, o supervisor aprova, o pedido FECHA e o
  // BKO valida o print no Pedido Fechado (ConvergenciaValidacaoBko). Antes, o
  // 'pendente' travava aqui e virava beco sem saída (não fechava e não havia fila
  // levando ao BKO). (Douglas 06/07)
  const emAnaliseConvergencia = !pedidoJaFechado && !!convStatus &&
    ((convStatus.status as string) === 'pendente_supervisor'
      || (convStatus.status as string) === 'pendente_gerente');

  // ✅ Helper para atualizar step
  const updateStep = useCallback((stepId: number, status: EnviarBKOStep['status']) => {
    setSteps(prev => prev.map(s => 
      s.id === stepId ? { ...s, status } : s
    ));
  }, []);

  // ✅ Função principal de fechamento
  // convergenciaResolvida: quando o modal de convergência já decidiu (re-entrada), pula o gate.
  const handleFecharPedido = async (convergenciaResolvida?: ConvergenciaPayload) => {
    // ✅ PROTEÇÃO IMEDIATA com useRef (mais rápido que state)
    if (isProcessingRef.current) {
      console.log('⏳ [BKO] Clique ignorado - já em processamento');
      return;
    }

    // Gerar ID único para esta execução
    const newRunId = `BKO_${pedido.id.substring(0, 8)}_${Date.now()}`;
    const t0 = performance.now();
    startTimeRef.current = t0;
    
    // Logger estruturado
    const log = (step: string, extra?: any) => {
      console.log(`🧾 [BKO] ${newRunId} :: ${step}`, { 
        ms: Math.round(performance.now() - t0), 
        ...extra 
      });
    };

    // Validação inicial
    if (!pedido?.id) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Dados do pedido não encontrados. Recarregue a página.',
      });
      return;
    }

    // ✅ ATIVAR LOCKS
    isProcessingRef.current = true;
    setIsProcessing(true);
    setOverlayOpen(true);
    setRunId(newRunId);
    setSteps(INITIAL_STEPS.map(s => ({ ...s, status: 'pending' })));
    setOverlayError(null);
    setElapsedMs(0);

    // Timer para atualizar tempo decorrido
    timerRef.current = setInterval(() => {
      setElapsedMs(performance.now() - startTimeRef.current);
    }, 100);

    log('INÍCIO', { pedidoId: pedido.id, pedidoNumero: pedido.numero_pedido });

    let pedidoFilhoCriado: any = null;
    let itensPedidoMovidos: string[] = [];
    let itensPedido: any[] = [];
    // true = filho reutilizado que JÁ tinha itens em PEDIDO FECHADO → rollback NUNCA pode deletá-lo
    let filhoTinhaItens = false;
    let convPayload: ConvergenciaPayload | null = convergenciaResolvida ?? null;
    const bloqueadoOriginal = !!pedido.bloqueado;
    let interrompido = false;
    const seguir = () => {
      if (interrompido) throw new Error('Processo interrompido');
    };

    // Timeout global
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Timeout: O processo demorou mais de 60 segundos')), TIMEOUT_GLOBAL_MS);
    });

    try {
      await Promise.race([
        (async () => {
          // ============= STEP 1: VALIDAÇÃO =============
          updateStep(1, 'loading');
          log('STEP 1 - Validação iniciada');

          const { data: pedidosFilhos, error: filhosError } = await supabase
            .from('pedidos')
            .select('id')
            .eq('pedido_mae_id', pedido.id);

          if (filhosError) throw filhosError;

          const pedidoIds = [pedido.id, ...(pedidosFilhos?.map(p => p.id) || [])];
          log('STEP 1 - Pedidos para validar', { total: pedidoIds.length });

          const resultado = await validarPedido(pedidoIds);

          if (!resultado) {
            throw new Error('Validação retornou resultado vazio');
          }

          if (!resultado.valido) {
            log('STEP 1 - Validação falhou - itens incompletos', { 
              incompletos: resultado.itensIncompletos?.length 
            });
            setValidacaoResultado(resultado);
            setAlertaOpen(true);
            setOverlayOpen(false);
            return;
          }

          // ✅ Validação de obrigatoriedades agora é feita via RPC que respeita as configurações do grupo
          // Removida validação hardcoded de tipo_chip - agora respeita grupo_obrigatoriedades_config

          // ✅ Validar Print Crédito antes de fechar (exceto MP Móvel)
          const { data: itensParaValidarPrint } = await supabase
            .from('itens_pedido')
            .select('produto_id')
            .in('pedido_id', pedidoIds);
          const produtoIds = itensParaValidarPrint?.map(i => i.produto_id).filter(Boolean) || [];

          const { data: clientePrint, error: erroClientePrint } = await supabase
            .from('clientes').select('tipo_pessoa').eq('id', pedido.cliente_id).single();
          if (erroClientePrint) throw erroClientePrint;

          if (clientePrint?.tipo_pessoa !== 'PF' && produtoIds.length > 0) {
            const { data: produtosInfo } = await supabase
              .from('produtos')
              .select('id, qtd_mp_movel')
              .in('id', produtoIds);

            const temNaoMpMovel = produtosInfo?.some(p => !p.qtd_mp_movel);

            if (temNaoMpMovel) {
              const { data: anexoPrint } = await supabase
                .from('anexos_crm')
                .select('id')
                .in('pedido_id', pedidoIds)
                .eq('tipo_anexo', 'PRINT_CREDITO')
                .limit(1);

              if (!anexoPrint || anexoPrint.length === 0) {
                toast({
                  variant: 'destructive',
                  title: 'Print Crédito obrigatório',
                  description: 'Inclua o anexo "Print Crédito" na aba Anexos antes de fechar o pedido. Apenas itens MP Móvel são isentos.',
                });
                setOverlayOpen(false);
                return;
              }
            }
          }

          updateStep(1, 'done');
          log('STEP 1 - Validação OK');

          // ===== GATE DE CONVERGÊNCIA (fail-open: erro aqui NÃO bloqueia o fechamento) =====
          if (!convPayload) {
            try {
              const existente = await getConvergenciaPedido(pedido.id);
              if (existente && statusJaResolvidoPassa(existente.status)) {
                convPayload = existente; // gerente/consultor já resolveu → passa direto
              } else if (existente && ((existente.status as string) === 'pendente_supervisor' || (existente.status as string) === 'pendente_gerente')) {
                // Já enviado pra análise (BKO/gerência) → NÃO reabre o modal nem
                // duplica a justificativa. Antes, reclicar em Fechar reabria o
                // modal e o consultor reenviava o mesmo "sem viabilidade" várias vezes.
                toast({
                  title: 'Já em análise',
                  description: 'Este pedido já está aguardando validação de convergência (BKO/gerência). Não precisa reenviar.',
                });
                setOverlayOpen(false);
                return;
              } else {
                const aval = await avaliarConvergenciaPedido(pedido);
                if (aval) {
                  const acao = aval.gate.acao;
                  if (acao === 'trava_movel_sem_fibra' || acao === 'trava_so_fibra' || acao === 'precisa_cep') {
                    log('GATE - trava de convergência', { acao });
                    setConvAval(aval);
                    setModalConvOpen(true);
                    setOverlayOpen(false);
                    return; // o finally libera os locks; o modal de convergência continua o fluxo
                  }
                  const { data: { user: uConv } } = await supabase.auth.getUser();
                  convPayload = montarConvergencia({
                    fatos: aval.fatos,
                    cobertura: aval.cobertura,
                    classificacao: aval.gate.classificacao,
                    status: statusAutoPassa(aval.gate.classificacao),
                    origem: 'auto',
                    resolucao: null,
                    decididoPor: pedido.consultor_id || uConv?.id || null,
                    agora: new Date().toISOString(),
                  });
                }
              }
            } catch (convErr: any) {
              console.warn('[convergencia] gate falhou, seguindo (fail-open):', convErr?.message);
            }
          }

          // ============= STEP 2: CRIAR PEDIDO FILHO =============
          updateStep(2, 'loading');
          log('STEP 2 - Criar pedido filho iniciado');

          const { data: { user } } = await supabase.auth.getUser();
          const consultorId = pedido.consultor_id || user?.id;

          if (!consultorId || !pedido.cliente_id) {
            throw new Error('Consultor ou Cliente não identificado');
          }

          // Buscar status de destino
          const { data: statusData, error: statusError } = await supabase
            .from('status_catalogo')
            .select('id')
            .eq('quadro', 'CONTRATO_PRIME')
            .eq('coluna', 'PEDIDO FECHADO')
            .eq('ativo', true)
            .maybeSingle();

          if (statusError || !statusData) {
            throw new Error('Status "PEDIDO FECHADO" não encontrado no sistema');
          }

          // Buscar itens
          // Estado original dos itens é capturado p/ o ROLLBACK restaurar quadro/coluna/status
          const { data: itensPedidoData, error: itensError } = await supabase
            .from('itens_pedido')
            .select('id, pedido_id, produto_id, quadro_atual, coluna_atual, status_id, entrada_coluna_em, status_ativacao')
            .in('pedido_id', pedidoIds);

          if (itensError) throw itensError;
          if (!itensPedidoData || itensPedidoData.length === 0) {
            throw new Error('Este pedido não possui itens para fechar');
          }

          const itensNoComercial = itensPedidoData.filter((i) => i.quadro_atual === 'COMERCIAL');
          if (itensNoComercial.length === 0) {
            throw new Error(
              `Nenhum dos ${itensPedidoData.length} item(ns) deste pedido está no COMERCIAL (já no BKO ou cancelados). ` +
              'Item cancelado se corrige em Revisar Proposta → Recuperar Pedido; o que já está no BKO não é enviado de novo.'
            );
          }

          itensPedido = itensNoComercial;
          log('STEP 2 - Itens encontrados', { total: itensPedido.length });

          // Verificar/criar pedido filho
          const { data: pedidosFilhosExistentes } = await supabase
            .from('pedidos')
            .select('id, numero_pedido')
            .eq('pedido_mae_id', pedido.id)
            .order('created_at', { ascending: false });

          let pedidoFilho: any;
          let pedidoReutilizado = false;

          // Verificar se já existe pedido filho em PEDIDO FECHADO
          if (pedidosFilhosExistentes && pedidosFilhosExistentes.length > 0) {
            const pedidoIdsFilhos = pedidosFilhosExistentes.map(p => p.id);
            const { data: itensEmPedidoFechado } = await supabase
              .from('itens_pedido')
              .select('pedido_id')
              .in('pedido_id', pedidoIdsFilhos)
              .eq('quadro_atual', 'CONTRATO_PRIME')
              .eq('coluna_atual', 'PEDIDO FECHADO')
              .limit(1);

            if (itensEmPedidoFechado && itensEmPedidoFechado.length > 0) {
              pedidoFilho = pedidosFilhosExistentes.find(p => p.id === itensEmPedidoFechado[0].pedido_id);
              if (pedidoFilho) {
                pedidoReutilizado = true;
                filhoTinhaItens = true;
                log('STEP 2 - Reutilizando pedido filho existente', { id: pedidoFilho.id });
                
                await supabase
                  .from('pedidos')
                  .update({
                    consultor_id: consultorId,
                    data_referencia: new Date().toISOString().split('T')[0],
                    origem_lead: pedido.origem_lead || 'MANUAL',
                    lead_origem_id: pedido.lead_origem_id,
                    total_faturamento: pedido.total_faturamento || 0,
                    total_venda_nova: pedido.total_venda_nova || 0,
                    total_migracao: pedido.total_migracao || 0,
                  })
                  .eq('id', pedidoFilho.id);
              }
            }
          }

          // ✅ IDEMPOTÊNCIA: retry reaproveita filho VAZIO de tentativa anterior.
          // Antes, cada "Tentar novamente" inseria OUTRA linha com o mesmo numero_pedido
          // (filho herda o número da mãe via fn_set_numero_pedido) → pedidos-fantasma.
          if (!pedidoFilho && pedidosFilhosExistentes && pedidosFilhosExistentes.length > 0) {
            const filhosComItens = new Set(itensPedido.map(i => i.pedido_id));
            const filhoVazio = pedidosFilhosExistentes.find(p => !filhosComItens.has(p.id));
            if (filhoVazio) {
              pedidoFilho = filhoVazio;
              pedidoReutilizado = true;
              log('STEP 2 - Reaproveitando filho VAZIO de tentativa anterior', { id: filhoVazio.id });

              await supabase
                .from('pedidos')
                .update({
                  consultor_id: consultorId,
                  bko_id: pedido.bko_id || null,
                  data_referencia: new Date().toISOString().split('T')[0],
                  origem_lead: pedido.origem_lead || 'MANUAL',
                  lead_origem_id: pedido.lead_origem_id,
                  origem: 'venda_consultor',
                  total_faturamento: pedido.total_faturamento || 0,
                  total_venda_nova: pedido.total_venda_nova || 0,
                  total_migracao: pedido.total_migracao || 0,
                })
                .eq('id', filhoVazio.id);
            }
          }

          if (!pedidoFilho) {
            log('STEP 2 - Criando novo pedido filho');
            const dataReferencia = new Date().toISOString().split('T')[0];

            const { data: pedidoFilhoNovo, error: errorFilho } = await supabase
              .from('pedidos')
              .insert([{
                cliente_id: pedido.cliente_id,
                consultor_id: consultorId,
                bko_id: pedido.bko_id || null,
                pedido_mae_id: pedido.id,
                data_referencia: dataReferencia,
                origem_lead: pedido.origem_lead || 'MANUAL',
                lead_origem_id: pedido.lead_origem_id,
                origem: 'venda_consultor',
                total_faturamento: pedido.total_faturamento || 0,
                total_venda_nova: pedido.total_venda_nova || 0,
                total_migracao: pedido.total_migracao || 0,
                numero_pedido: 0,
              }])
              .select()
              .single();

            if (errorFilho) throw errorFilho;
            pedidoFilho = pedidoFilhoNovo;
          }

          pedidoFilhoCriado = pedidoFilho;
          updateStep(2, 'done');
          log('STEP 2 - Pedido filho OK', { id: pedidoFilho.id, numero: pedidoFilho.numero_pedido });

          // ============= STEP 3: MOVER ITENS =============
          seguir();
          updateStep(3, 'loading');
          log('STEP 3 - Mover itens iniciado');

          const { data: movedItems, error: errorMoverItens } = await supabase
            .from('itens_pedido')
            .update({ pedido_id: pedidoFilho.id })
            .in('id', itensPedido.map(i => i.id))
            .select('id');

          if (errorMoverItens) throw errorMoverItens;

          if (!movedItems || movedItems.length !== itensPedido.length) {
            throw new Error(`Falha ao mover itens: ${movedItems?.length || 0} de ${itensPedido.length}`);
          }

          itensPedidoMovidos = movedItems.map(i => i.id);
          updateStep(3, 'done');
          log('STEP 3 - Itens movidos', { total: movedItems.length });

          // ============= STEP 3.5: COPIAR ANEXOS PRINT_CREDITO PARA PEDIDO FILHO =============
          try {
            // Filho reaproveitado pode já ter a cópia da tentativa anterior — não duplicar
            const { data: anexosJaNoFilho } = await supabase
              .from('anexos_crm')
              .select('id')
              .eq('pedido_id', pedidoFilho.id)
              .eq('tipo_anexo', 'PRINT_CREDITO')
              .limit(1);

            const { data: anexosMae } = await supabase
              .from('anexos_crm')
              .select('*')
              .eq('pedido_id', pedido.id)
              .eq('tipo_anexo', 'PRINT_CREDITO');

            if ((!anexosJaNoFilho || anexosJaNoFilho.length === 0) && anexosMae && anexosMae.length > 0) {
              const anexosFilho = anexosMae.map(a => ({
                pedido_id: pedidoFilho.id,
                cliente_id: a.cliente_id,
                tipo_anexo: a.tipo_anexo,
                nome: a.nome,
                path: a.path,
                mime: a.mime,
                tamanho: a.tamanho,
                tipo_extensao: a.tipo_extensao,
                usuario_id: a.usuario_id,
              }));
              await supabase.from('anexos_crm').insert(anexosFilho);
              log('STEP 3.5 - Anexos PRINT_CREDITO copiados para pedido filho', { total: anexosMae.length });
            }
          } catch (anexoErr: any) {
            log('STEP 3.5 - Erro ao copiar anexos (não crítico, trigger aceita pedido mãe)', { error: anexoErr.message });
          }

          // ============= STEP 4: ATUALIZAR STATUS =============
          seguir();
          updateStep(4, 'loading');
          log('STEP 4 - Atualizar status iniciado');

          const agora = new Date().toISOString();
          const itemIds = itensPedido.map(i => i.id);

          // ✅ CORREÇÃO DEADLOCK: Unificado updates de itens_pedido em um único comando
          const [updateItensResult, , bloqueioMaeResult] = await Promise.all([
            supabase
              .from('itens_pedido')
              .update({
                quadro_atual: 'CONTRATO_PRIME',
                coluna_atual: 'PEDIDO FECHADO',
                status_id: statusData.id,
                entrada_coluna_em: agora,
                updated_at: agora,
                status_ativacao: 'pendente'//  Incluído aqui para evitar deadlock
              })
              .in('id', itemIds),
            supabase.from('pedidos').update({ tem_item: true }).eq('id', pedidoFilho.id),
            supabase.from('pedidos').update({ bloqueado: true, origem: 'venda_consultor' }).eq('id', pedido.id)
          ]);

          if (updateItensResult.error) {
            throw new Error(`Falha ao mover itens para PEDIDO FECHADO: ${updateItensResult.error.message}`);
          }

          // Registrar histórico em batch
          const historicoInserts = itensPedido.map(item => ({
            item_id: item.id,
            pedido_id: pedidoFilho.id,
            status_atual_id: statusData.id,
            coluna_atual: 'PEDIDO FECHADO',
            quadro_atual: 'CONTRATO_PRIME' as const,
            user_id: consultorId,
            motivo: 'Fechamento de pedido'
          }));

          await supabase.from('historico_status').insert(historicoInserts);

          updateStep(4, 'done');
          log('STEP 4 - Status atualizado');

          // Grava o controle de convergência no pedido filho (fechado). Não crítico.
          if (convPayload) {
            try { await gravarConvergencia(pedidoFilho.id, convPayload); }
            catch (e: any) { console.warn('[convergencia] gravar no filho falhou:', e?.message); }
          }

          // ============= STEP 5: ATRIBUIR BKO =============
          seguir();
          updateStep(5, 'loading');
          log('STEP 5 - Atribuir BKO iniciado');

          let bkoNome: string | null = null;
          let bkoIdFinal: string | null = null;

          try {
            const { data: bkoAtribuido, error: bkoError } = await supabase
              .rpc('atribuir_bko_criacao', { p_pedido_id: pedidoFilho.id })
              .single();

            if (bkoError) {
              log('STEP 5 - Erro atribuir_bko_criacao, tentando fallback', { error: bkoError.message });
              const { data: bkoFallback } = await supabase
                .rpc('atribuir_bko_disponivel', { p_pedido_id: pedidoFilho.id })
                .single();
              if (bkoFallback) {
                bkoNome = bkoFallback.bko_nome || null;
                bkoIdFinal = bkoFallback.bko_id || null;
              }
            } else if (bkoAtribuido) {
              bkoNome = bkoAtribuido.bko_nome || null;
              bkoIdFinal = bkoAtribuido.bko_id || null;
            }
          } catch (bkoErr: any) {
            log('STEP 5 - Erro ao atribuir BKO (não crítico)', { error: bkoErr.message });
          }

          // Notificar BKO
          if (bkoIdFinal) {
            await supabase.from('notificacoes').insert({
              usuario_id: bkoIdFinal,
              tipo: 'novo_pedido_contrato',
              titulo: 'Novo Pedido para Criar Contrato!',
              mensagem: `Pedido #${pedidoFilhoCriado.numero_pedido} está pronto para criar contrato.`,
              pedido_id: pedidoFilho.id,
              prioridade: 'alta'
            });
          }

          updateStep(5, 'done');
          log('STEP 5 - BKO OK', { bkoNome });

          // ============= STEP 6: FINALIZAR =============
          updateStep(6, 'loading');
          log('STEP 6 - Finalizar iniciado');

          // Calcular KPIs em background (não bloqueia)
          setTimeout(async () => {
            try {
              const produtoIds = itensPedido.map((i: any) => i.produto_id).filter(Boolean);
              if (produtoIds.length > 0) {
                const { data: produtosKPI } = await supabase
                  .from('produtos')
                  .select('id, nome, fat_total, qtd_altas, qtd_fibra, qtd_mp_movel, qtd_pn, pontos_1')
                  .in('id', produtoIds);

                if (produtosKPI) {
                  for (const item of itensPedido) {
                    const produtoKPI = produtosKPI.find((p: any) => p.id === item.produto_id);
                    if (!produtoKPI) continue;

                    const { data: itemCompleto } = await supabase
                      .from('itens_pedido')
                      .select('subtotal, quantidade, faturamento_tipo')
                      .eq('id', item.id)
                      .single();

                    if (itemCompleto) {
                      const kpisAtualizados: Record<string, number> = {};
                      if (produtoKPI.fat_total) kpisAtualizados.fat_total = Number(itemCompleto.subtotal) || 0;
                      if (ehVendaNova(itemCompleto.faturamento_tipo)) kpisAtualizados.fat_venda_nova = Number(itemCompleto.subtotal) || 0;
                      if (ehMigracao(itemCompleto.faturamento_tipo)) kpisAtualizados.fat_migracao = Number(itemCompleto.subtotal) || 0;
                      if (produtoKPI.qtd_altas) kpisAtualizados.qtd_altas = itemCompleto.quantidade || 0;
                      if (produtoKPI.qtd_fibra) kpisAtualizados.qtd_fibra = itemCompleto.quantidade || 0;
                      if (produtoKPI.qtd_mp_movel) kpisAtualizados.qtd_mp_movel = itemCompleto.quantidade || 0;
                      if (produtoKPI.qtd_pn) kpisAtualizados.qtd_pn = itemCompleto.quantidade || 0;
                      if (produtoKPI.pontos_1) kpisAtualizados.pontos_1 = itemCompleto.quantidade || 0;

                      await supabase
                        .from('itens_pedido')
                        .update({ kpis_calculados: kpisAtualizados })
                        .eq('id', item.id);
                    }
                  }
                }
              }
            } catch (kpiError) {
              console.warn('⚠️ [BKO] Erro ao calcular KPIs (background):', kpiError);
            }
          }, 100);

          // Limpeza de órfãos em background
          setTimeout(async () => {
            try {
              const { data: todosFilhos } = await supabase
                .from('pedidos')
                .select('id')
                .eq('pedido_mae_id', pedido.id)
                .neq('id', pedidoFilho.id);

              if (todosFilhos && todosFilhos.length > 0) {
                const { data: pedidosComItens } = await supabase
                  .from('itens_pedido')
                  .select('pedido_id')
                  .in('pedido_id', todosFilhos.map(f => f.id));

                const pedidosComItensIds = new Set(pedidosComItens?.map(i => i.pedido_id) || []);
                const pedidosOrfaos = todosFilhos.filter(f => !pedidosComItensIds.has(f.id));

                if (pedidosOrfaos.length > 0) {
                  // Dependências primeiro, senão o DELETE falha mudo por FK e o fantasma fica
                  const orfaoIds = pedidosOrfaos.map(p => p.id);
                  await supabase.from('anexos_crm').delete().in('pedido_id', orfaoIds);
                  await supabase.from('notificacoes').delete().in('pedido_id', orfaoIds);
                  await supabase.from('historico_status').delete().in('pedido_id', orfaoIds);
                  await supabase.from('pedidos').delete().in('id', orfaoIds);
                }
              }
            } catch (cleanupError) {
              console.warn('⚠️ [BKO] Erro na limpeza de órfãos (background):', cleanupError);
            }
          }, 200);

          // Invalidar cache
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: ['v_planilha_mae'], refetchType: 'active' }),
            queryClient.invalidateQueries({ queryKey: ['planilha-itens'], refetchType: 'active' }),
            queryClient.invalidateQueries({ queryKey: ['kanban'], exact: false, refetchType: 'active' }),
            queryClient.invalidateQueries({ queryKey: ['pedido', pedido.id] }),
            queryClient.invalidateQueries({ queryKey: ['pedido', pedidoFilho.id] }),
            queryClient.invalidateQueries({ queryKey: ['venda-diaria'] }),
            queryClient.invalidateQueries({ queryKey: ['check-pedido-fechado', pedido.id], refetchType: 'active' }),
            // ✅ Painel 360 / ItensTab - Corrige status "AGUARDANDO INTERAÇÃO" → "PEDIDO FECHADO"
            queryClient.invalidateQueries({ queryKey: ['pedido-itens-completo', pedido.id], refetchType: 'active' }),
            queryClient.invalidateQueries({ queryKey: ['pedido-itens-completo', pedidoFilho.id], refetchType: 'active' }),
            queryClient.invalidateQueries({ queryKey: ['item-completo'], exact: false, refetchType: 'active' }),
            // ✅ Visão 360 do Cliente - Atualiza lista de pedidos
            queryClient.invalidateQueries({ queryKey: ['pedidos-cliente-visao360'], exact: false, refetchType: 'active' }),
            // ✅ Leads - Atualiza status CONVERTIDO → CARTEIRIZADO
            queryClient.invalidateQueries({ queryKey: ['leads-carteira'], exact: false, refetchType: 'active' }),
            queryClient.invalidateQueries({ queryKey: ['meus-leads'], exact: false, refetchType: 'active' }),
            queryClient.invalidateQueries({ queryKey: ['leads-carteira-resumo'], exact: false, refetchType: 'active' }),
          ]);

          await queryClient.refetchQueries({ queryKey: ['kanban'], exact: false });

          updateStep(6, 'done');
          log('STEP 6 - Finalizado com sucesso!', { 
            pedidoFilhoId: pedidoFilho.id,
            pedidoFilhoNumero: pedidoFilho.numero_pedido,
            bkoNome,
            tempo: Math.round(performance.now() - t0)
          });

          // Fechar overlay e abrir modal de sucesso
          setOverlayOpen(false);
          setDadosSucesso({
            pedidoNumero: pedidoFilhoCriado.numero_pedido,
            bkoNome: bkoNome,
            pedidoId: pedidoFilhoCriado.id
          });
          setModalSucessoOpen(true);
        })(),
        timeoutPromise
      ]);

    } catch (error: any) {
      interrompido = true;
      const errorMsg = error?.message || 'Erro desconhecido';
      console.error(`🧾 [BKO] ${runId} :: ERRO CRÍTICO`, { 
        error: errorMsg,
        pedidoFilhoCriado: pedidoFilhoCriado?.id,
        itensPedidoMovidos: itensPedidoMovidos.length
      });

      // Marcar step atual como erro
      setSteps(prev => prev.map(s => ({
        ...s,
        status: s.status === 'loading' ? 'error' : s.status
      })));
      setOverlayError(errorMsg);

      // ✅ ROLLBACK: devolve itens ao lugar/coluna ORIGINAIS e apaga o filho desta
      // tentativa junto com as dependências (anexos copiados no 3.5, histórico,
      // notificações) — eram elas que travavam o DELETE por FK e deixavam fantasma.
      // Se algo falhar aqui, tudo bem: o retry agora REAPROVEITA o filho vazio.
      if (pedidoFilhoCriado) {
        console.log(`🧾 [BKO] ${runId} :: ROLLBACK - Iniciando`);
        try {
          let itensRestaurados = true;
          if (itensPedidoMovidos.length > 0) {
            const restaurar = (item: any) => supabase
              .from('itens_pedido')
              .update({
                pedido_id: item.pedido_id,
                quadro_atual: item.quadro_atual,
                coluna_atual: item.coluna_atual,
                status_id: item.status_id,
                entrada_coluna_em: item.entrada_coluna_em,
                status_ativacao: item.status_ativacao,
              })
              .eq('id', item.id)
              .select('id, coluna_atual');
            let pendentes = itensPedido;
            for (let tentativa = 0; tentativa < 3 && pendentes.length > 0; tentativa++) {
              const restauros = await Promise.all(pendentes.map(restaurar));
              pendentes = pendentes.filter((item, i) => {
                const r = restauros[i];
                return !!r.error || r.data?.[0]?.coluna_atual !== item.coluna_atual;
              });
            }
            await new Promise(r => setTimeout(r, 2000));
            const { data: agora } = await supabase
              .from('itens_pedido').select('id, pedido_id, coluna_atual').in('id', itensPedido.map(i => i.id));
            const fora = itensPedido.filter(item => {
              const a = (agora || []).find((x: any) => x.id === item.id);
              return !a || a.coluna_atual !== item.coluna_atual || a.pedido_id !== item.pedido_id;
            });
            if (fora.length > 0) {
              const segunda = await Promise.all(fora.map(restaurar));
              pendentes = fora.filter((item, i) => !!segunda[i].error || segunda[i].data?.[0]?.coluna_atual !== item.coluna_atual);
            }
            itensRestaurados = pendentes.length === 0;
            if (!itensRestaurados) {
              setOverlayError(`${errorMsg}. ${pendentes.length} item(ns) não voltaram ao status anterior: avise o suporte antes de tentar de novo.`);
            }
          }
          if (itensRestaurados) {
            await supabase.from('pedidos').update({ bloqueado: bloqueadoOriginal }).eq('id', pedido.id);
          }

          if (!filhoTinhaItens && itensRestaurados) {
            await supabase.from('anexos_crm').delete().eq('pedido_id', pedidoFilhoCriado.id);
            await supabase.from('notificacoes').delete().eq('pedido_id', pedidoFilhoCriado.id);
            await supabase.from('historico_status').delete().eq('pedido_id', pedidoFilhoCriado.id);
            const { error: delError } = await supabase
              .from('pedidos')
              .delete()
              .eq('id', pedidoFilhoCriado.id);
            if (delError) {
              console.warn(`🧾 [BKO] ${runId} :: ROLLBACK - filho não deletado (retry vai reaproveitar)`, { error: delError.message });
            }
          }

          console.log(`🧾 [BKO] ${runId} :: ROLLBACK - Concluído`, { itensRestaurados });
        } catch (rollbackError: any) {
          console.error(`🧾 [BKO] ${runId} :: ROLLBACK - Erro`, { error: rollbackError.message });
        }
      }

    } finally {
      // ✅ SEMPRE liberar locks
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      isProcessingRef.current = false;
      setIsProcessing(false);
      console.log(`🧾 [BKO] ${runId} :: FINALLY - Lock liberado`);
    }
  };

  // Resolução do modal de convergência
  const handleResolverConvergencia = async (r: ResolverConvergencia) => {
    setModalConvOpen(false);
    if (!convAval) return;
    const { data: { user } } = await supabase.auth.getUser();
    const decididoPor = pedido.consultor_id || user?.id || null;
    const cobertura = r.cepInformado != null
      ? { ...convAval.cobertura, cep: r.cepInformado, temCobertura: r.coberturaInformada ?? convAval.cobertura.temCobertura }
      : convAval.cobertura;
    const origem: ConvergenciaPayload['origem_decisao'] =
      r.acao === 'gerente' ? 'consultor'
        : r.fixaJaExistente ? 'modal_cliente_tem_fibra'
          : r.qtdLinhasMovel != null ? 'modal_informou_movel'
            : 'auto';
    // Convergência anterior do pedido — p/ o ciclo de reenvio (rodada + histórico).
    const anterior = await getConvergenciaPedido(pedido.id);
    const payload = montarConvergencia({
      fatos: convAval.fatos,
      cobertura,
      classificacao: r.classificacao,
      // Sem aprovação do supervisor (Douglas 08/10/2026): o consultor declara e o
      // pedido segue direto ao BKO, que confere o print quando houver.
      status: r.acao === 'gerente' ? 'aprovado sem fibra' : (r.status ?? null),
      origem,
      resolucao: r,
      decididoPor,
      agora: new Date().toISOString(),
      anterior,
    });
    if (r.acao === 'passa') {
      // Fibra fora do nosso território → solicitação de carteira: registra na timeline do CNPJ.
      if (payload.status === 'solicitação de carteira') {
        try {
          await registrarTimelineConvergencia({
            pedidoId: pedido.id,
            cnpj: convAval.cnpj,
            descricao: `Convergência: solicitação de carteira aberta (fibra fora do nosso território${convAval.cobertura.territorioAace ? ` — ${convAval.cobertura.territorioAace}` : ''})`,
            usuarioId: decididoPor,
            payload,
            destaque: true,
          });
        } catch (e: any) { console.warn('[convergencia] timeline carteira falhou:', e?.message); }
      }
      // Persiste a convergência no pedido (pro BKO validar quando for auto-declarada
      // "já tem fibra/móvel" → bko_validacao='pendente') + registra na timeline.
      try {
        await gravarConvergencia(pedido.id, payload);
        if (payload.bko_validacao === 'pendente') {
          const oque = payload.fixa_ja_existente
            ? `cliente já tem FIBRA (contrato ${payload.fixa_ja_existente.numeroContrato || '-'} · ${payload.fixa_ja_existente.endereco || '-'})`
            : `cliente já tem MÓVEL (${payload.qtd_linhas_movel ?? '-'} linhas · ${payload.territorio_aace || '-'})`;
          await registrarTimelineConvergencia({
            pedidoId: pedido.id,
            cnpj: convAval.cnpj,
            descricao: `Convergência (Consultor declarou): ${oque} — aguarda validação do BKO`,
            usuarioId: decididoPor,
            payload,
            destaque: true,
          });
        }
      } catch (e: any) { console.warn('[convergencia] gravar/timeline passa falhou:', e?.message); }
      handleFecharPedido(payload); // re-entra pulando o gate
    } else {
      // Sem fibra declarado pelo consultor: grava a convergência na família e
      // fecha o pedido direto para o BKO. Com print, o BKO confere no Pedido
      // Fechado (ConvergenciaValidacaoBko); sem print, vale a declaração (tabela 2).
      if (!decididoPor) throw new Error('Usuário não identificado para enviar a convergência.');
      try {
        await gravarConvergencia(pedido.id, payload);
      } catch (e: any) {
        toast({
          title: 'Convergência não gravada',
          description: e?.message || 'Nada foi movimentado. Tente novamente.',
          variant: 'destructive',
        });
        setModalConvOpen(true);
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['convergencia-status-botao', pedido.id] });
      const temProva = !!payload.prova_url;
      await registrarTimelineConvergencia({
        pedidoId: pedido.id,
        cnpj: convAval.cnpj,
        descricao: temProva
          ? `Convergência (Consultor): SEM viabilidade de fibra no endereço da Receita — print anexado, aguarda conferência do BKO. ${r.motivo?.trim() || ''}`.trim()
          : `Convergência (Consultor · sem fibra): ${r.motivo?.trim() || '(sem justificativa)'}`,
        usuarioId: decididoPor,
        payload,
        destaque: temProva,
      });
      handleFecharPedido(payload);
    }
  };

  const handleRetry = () => {
    setOverlayOpen(false);
    setOverlayError(null);
    setTimeout(() => {
      handleFecharPedido();
    }, 100);
  };

  const handleCloseOverlay = () => {
    setOverlayOpen(false);
    setOverlayError(null);
  };

  const handleCompletarItens = () => {
    setAlertaOpen(false);
    painelPedido.open(pedido.id, 'kanban');
  };

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="w-full">
              <Button
                onClick={() => handleFecharPedido()}
                disabled={validando || isMoving || checkingStatus || pedidoJaFechado || isProcessing || emAnaliseConvergencia}
                className="w-full min-w-[200px]"
                variant={pedidoJaFechado || emAnaliseConvergencia ? "secondary" : "default"}
              >
                {checkingStatus ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verificando...
                  </>
                ) : validando || isMoving || isProcessing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {isProcessing ? 'ENVIANDO...' : validando ? 'Validando...' : 'Fechando...'}
                  </>
                ) : pedidoJaFechado ? (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Já Enviado ao BKO
                  </>
                ) : emAnaliseConvergencia ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2" />
                    Em análise de convergência (BKO/gerência)
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    {label || 'Enviar ao BKO'}
                  </>
                )}
              </Button>
            </div>
          </TooltipTrigger>
          {!pedidoJaFechado && !emAnaliseConvergencia && (
            <TooltipContent>
              <p>Mover todos os itens para SUPORTE COMERCIAL / PEDIDO FECHADO</p>
            </TooltipContent>
          )}
          {emAnaliseConvergencia && (
            <TooltipContent>
              <p className="text-sm max-w-xs">Pedido enviado para análise de convergência. Aguarde a validação do BKO/gerência — não precisa reenviar.</p>
            </TooltipContent>
          )}
          {pedidoJaFechado && (
            <TooltipContent>
              <p className="text-sm">
                {pedido.pedido_mae_id 
                  ? 'Este pedido é uma versão fechada (filho) e não pode ser fechado novamente'
                  : 'Este pedido já foi fechado e movido para SUPORTE COMERCIAL / PEDIDO FECHADO'
                }
              </p>
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>

      {/* Overlay de Progresso */}
      <EnviarBKOOverlay
        isOpen={overlayOpen}
        currentStep={steps.filter(s => s.status === 'done').length}
        steps={steps}
        runId={runId}
        elapsedMs={elapsedMs}
        errorMessage={overlayError}
        onRetry={handleRetry}
        onClose={handleCloseOverlay}
      />

      {validacaoResultado && (
        <AlertaItensIncompletosModal
          open={alertaOpen}
          onOpenChange={setAlertaOpen}
          itensIncompletos={validacaoResultado.itensIncompletos}
          totalItens={validacaoResultado.totalItens}
          itensCompletos={validacaoResultado.itensCompletos}
          onCompletarItens={handleCompletarItens}
        />
      )}

      {dadosSucesso && (
        <ModalSucessoFechamento
          open={modalSucessoOpen}
          onOpenChange={setModalSucessoOpen}
          pedidoNumero={dadosSucesso.pedidoNumero}
          bkoNome={dadosSucesso.bkoNome}
          onVerPedido={() => {
            setModalSucessoOpen(false);
            painelPedido.open(dadosSucesso.pedidoId, 'kanban');
          }}
        />
      )}

      <ModalConvergencia
        open={modalConvOpen}
        onOpenChange={setModalConvOpen}
        aval={convAval}
        processing={isProcessing}
        onResolver={handleResolverConvergencia}
      />
    </>
  );
}
